import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Film, Loader2, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { CHAT_VIDEO_EVENT, getChatVideoTasks, resumeChatVideos, setChatVideoOwner, dismissChatVideo, retryChatVideo } from '../../services/chatVideoQueue';
import './ChatVideoStatus.css';

export function useChatVideos(ownerId) {
  const [tasks, setTasks] = useState(getChatVideoTasks);
  useEffect(() => {
    const refresh = () => setTasks(getChatVideoTasks());
    refresh();
    window.addEventListener(CHAT_VIDEO_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => { window.removeEventListener(CHAT_VIDEO_EVENT, refresh); window.removeEventListener('storage', refresh); };
  }, []);
  return useMemo(() => tasks.filter(t => t.ownerId === ownerId), [tasks, ownerId]);
}

const labels = {
  UPLOADING: ['جاري رفع الفيديو…', 'Uploading video…'],
  PROCESSING: ['جاري عزل الصوت…', 'Isolating vocals…'],
  SENDING: ['جاري إرسال الفيديو…', 'Sending video…'],
  SENT: ['تم إرسال الفيديو', 'Video sent'],
  FAILED: ['تعذر إرسال الفيديو', 'Video could not be sent'],
};
export function PendingChatVideo({ task, language = 'ar', onOpen }) {
  const ar = language === 'ar';
  const failed = task.stage === 'FAILED';
  const done = task.stage === 'SENT';
  const busy = !failed && !done;
  return <article className={`pending-chat-video ${failed ? 'is-failed' : ''}`} aria-label={ar ? 'حالة إرسال الفيديو' : 'Video send status'}>
    <header><Film size={20}/><strong dir="auto">{task.fileName}</strong></header>
    {task.caption && <p dir="auto">{task.caption}</p>}
    <div className="pending-chat-video-status" role="status">
      {busy ? <Loader2 size={16} className="pending-video-spin"/> : failed ? <AlertCircle size={16}/> : <Check size={16}/>}
      <span>{labels[task.stage]?.[ar ? 0 : 1]}</span>
    </div>
    {busy && <progress aria-label={ar ? 'تقدم الإرسال' : 'Sending progress'} />}
    {failed && <small>{task.uncertain ? (ar ? 'تحقق من المحادثة: ربما تم الإرسال بالفعل.' : 'Check the conversation: the video may already have been sent.') : task.error}</small>}
    <footer>
      {onOpen && <button type="button" onClick={onOpen}>{ar ? 'فتح المحادثة' : 'Open chat'}</button>}
      {failed && !task.uncertain && <button type="button" onClick={() => retryChatVideo(task.id)}>{ar ? 'إعادة المحاولة' : 'Retry'}</button>}
      {(failed || done) && <button type="button" onClick={() => dismissChatVideo(task.id)}>{ar ? 'إغلاق' : 'Dismiss'}</button>}
    </footer>
  </article>;
}

export default function ChatVideoStatus() {
  const { currentUserId } = useAuth();
  const { language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const tasks = useChatVideos(currentUserId);
  useEffect(() => { resumeChatVideos(currentUserId); return () => setChatVideoOwner(null); }, [currentUserId]);
  const task = tasks.find(t => t.route !== location.pathname && t.stage !== 'SENT') || tasks.find(t => t.route !== location.pathname);
  if (!task) return null;
  return <aside className="chat-video-toast" dir={language === 'ar' ? 'rtl' : 'ltr'}>
    <PendingChatVideo task={task} language={language} onOpen={() => navigate(task.route)} />
  </aside>;
}
